import React from 'react'
import {
  InspectorLayersTreeBasicArtboardLayerNode,
  InspectorLayersTreeSymbolMasterArtboardLayerNode,
  useInspectorContext,
  useUserColorFormat,
  InspectorLayersTreeRegularLayerNode,
} from '../../hooks/inspector'
import {
  PositionAndSize,
  Overview,
  InspectorSkeleton,
  LayoutSettings,
  Exports,
  GridSettings,
  InspectorError,
  ArtboardOverview,
  Corners,
  Appearance,
  Text,
  TextContent,
  Style,
  TextOrLayerStyles,
  SymbolInSidebar,
} from './sections'
import { Box, Flex } from '@sketch/components'

import * as S from './ArtboardDetailInspector.styles'
import {
  useEditableComponentDescription,
  useSetupComponentDescriptions,
} from './useEditableComponentDescription'
import { EditableComponentDescription } from 'modules/shares/ComponentsWebView/components/ComponentDescription'
import { ShareWithoutVersion } from 'modules/versioning'
import { ArtboardDetailInfoFragment, VersionFragment } from '@sketch/gql-types'
import { TextLayerStyle } from '../../hooks/inspector/inspectorDataType/TextLayer'
import { isArtboard } from '../../hooks/inspector/sketchSceneLib/inspectorTreeNodeUtilsDeprecated'

type ArtboardDetailInspectorProps = {
  share: ShareWithoutVersion
  artboard: ArtboardDetailInfoFragment
  currentVersion: VersionFragment | undefined
}

export function ArtboardDetailInspector({
  share,
  artboard,
  currentVersion,
}: ArtboardDetailInspectorProps) {
  const {
    selectedNode,
    layersTreeRootNode,
    isInspectorReady,
    hasInspectorDataError,
  } = useInspectorContext()

  // Start fetching before isInspectorReady is true
  useSetupComponentDescriptions(share.identifier, artboard, currentVersion)

  const inspectedNode = selectedNode ? selectedNode : layersTreeRootNode

  if (hasInspectorDataError) {
    /** @todo better handling of errors coming from backend. */
    return <InspectorError error="DEFAULT" />
  }

  if (!isInspectorReady) {
    return <InspectorSkeleton />
  }

  if (!inspectedNode) {
    return null
  }

  return (
    <S.Container>
      {isArtboard(inspectedNode) ? (
        <ArtboardLayerInfo
          artboardNode={inspectedNode}
          currentVersion={currentVersion}
        />
      ) : (
        <RegularLayerInfo
          currentVersion={currentVersion}
          layerNode={inspectedNode}
          shareIdentifier={share.identifier}
        />
      )}
    </S.Container>
  )
}

type ArtboardLayerInfoProps = {
  artboardNode:
    | InspectorLayersTreeBasicArtboardLayerNode
    | InspectorLayersTreeSymbolMasterArtboardLayerNode
  currentVersion: VersionFragment | undefined
}

function ArtboardLayerInfo({
  artboardNode,
  currentVersion,
}: ArtboardLayerInfoProps) {
  const [colorFormat, setColorFormat] = useUserColorFormat()
  const { hasAnyLayerWithExportableAssets } = useInspectorContext()

  const {
    description,
    isEnabled,
    editDescriptionHandler,
  } = useEditableComponentDescription()

  return (
    <>
      <Overview layerNode={artboardNode} />
      {artboardNode.type === 'symbolMaster' && (
        <EditableComponentDescription
          description={description}
          disabled={!isEnabled}
          onEdit={editDescriptionHandler}
        />
      )}
      <ArtboardOverview
        color={artboardNode.backgroundColor}
        preset={artboardNode.preset}
        frame={artboardNode.rootRelativeBounds}
        colorFormat={colorFormat}
        onColorFormatChange={setColorFormat}
      />
      <LayoutSettings layout={artboardNode.layoutSettings} />
      <GridSettings grid={artboardNode.gridSettings} />
      {currentVersion && artboardNode.exportId && (
        <Box marginTop="auto">
          <Exports
            exportType="artboard"
            exportFormats={artboardNode.exportFormats}
            hasOtherExportableLayers={hasAnyLayerWithExportableAssets}
            currentVersion={currentVersion}
            exportableAssetId={artboardNode.exportId}
          />
        </Box>
      )}
    </>
  )
}

type RegularLayerInfoProps = {
  layerNode: InspectorLayersTreeRegularLayerNode
  currentVersion: VersionFragment | undefined
  shareIdentifier: string
}
function RegularLayerInfo({
  layerNode,
  currentVersion,
  shareIdentifier,
}: RegularLayerInfoProps) {
  const { hasAnyLayerWithExportableAssets } = useInspectorContext()
  const [colorFormat, setColorFormat] = useUserColorFormat()
  const isDirty =
    layerNode.style && 'isDirty' in layerNode.style
      ? layerNode.style?.isDirty
      : false
  return (
    <>
      <Flex
        width="100%"
        alignItems="center"
        justifyContent="space-between"
        pr={5}
      >
        <Overview layerNode={layerNode} />
      </Flex>
      <PositionAndSize
        x={layerNode.rootRelativeBounds.x}
        y={layerNode.rootRelativeBounds.y}
        width={layerNode.rootRelativeBounds.width}
        height={layerNode.rootRelativeBounds.height}
        rotate={layerNode.rotation}
      />
      {layerNode.textStyleComponent && currentVersion && (
        <TextOrLayerStyles
          title="Text Styles"
          isDirty={isDirty}
          isTextStyleComponent={true}
          shareIdentifier={shareIdentifier}
          currentVersion={currentVersion}
          textOrLayerStyle={layerNode.textStyleComponent}
        />
      )}
      {layerNode.layerStyleComponent && currentVersion && (
        <TextOrLayerStyles
          title="Layer Styles"
          isDirty={isDirty}
          isTextStyleComponent={false}
          shareIdentifier={shareIdentifier}
          currentVersion={currentVersion}
          textOrLayerStyle={layerNode.layerStyleComponent}
        />
      )}
      {layerNode.corners && <Corners corners={layerNode.corners} />}
      {layerNode.appearance && <Appearance appearance={layerNode.appearance} />}
      <TextSection layerNode={layerNode} />
      {layerNode.style && (
        <Style
          style={layerNode.style}
          onColorFormatChange={setColorFormat}
          colorFormat={colorFormat}
          layerType={layerNode.type}
        />
      )}
      {layerNode.text && <TextContent>{layerNode.text.string}</TextContent>}
      {(layerNode.type === 'symbolInstance' || layerNode.parentSymbolLayer) && (
        <SymbolInSidebar layerNode={layerNode} />
      )}
      {currentVersion && layerNode.exportId && (
        <Box marginTop="auto">
          <Exports
            exportType="layer"
            exportFormats={layerNode.exportFormats}
            hasOtherExportableLayers={hasAnyLayerWithExportableAssets}
            currentVersion={currentVersion}
            exportableAssetId={layerNode.exportId}
          />
        </Box>
      )}
    </>
  )
}

type TextSectionProps = { layerNode: InspectorLayersTreeRegularLayerNode }

function TextSection({ layerNode }: TextSectionProps) {
  const [colorFormat, setColorFormat] = useUserColorFormat()

  if (layerNode.type !== 'text' || !layerNode.text) {
    return null
  }

  /**
   * It would have been nice to have this typed automatically based on the layerNode.type
   * but that's something we could improve in the future.
   */
  const style = (layerNode.style as unknown) as TextLayerStyle

  return (
    <Text
      attributedString={layerNode.text}
      verticalAlignment={style.text.verticalAlignment}
      originalSharedStyleValues={style.originalSharedStyleValues}
      onColorFormatChange={setColorFormat}
      colorFormat={colorFormat}
    />
  )
}
